import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { API } from "@/lib/api";
import { AdminDashboard } from "./AdminDashboard";
import { IncomesPanel } from "./IncomesPanel";
import { ExpensesPanel } from "./ExpensesPanel";
import { SalesGoalsPanel } from "./SalesGoalsPanel";
import { CommissionsPanel } from "./CommissionsPanel";
import { AttendancePanel } from "./AttendancePanel";
import { ServicesPanel } from "./ServicesPanel";
import { SalesCommissionsPanel } from "./SalesCommissionsPanel";
import { PendingApprovalsPanel } from "./PendingApprovalsPanel";
import { PettyCashPanel } from "./PettyCashPanel";
import { FinancialReportsPanel } from "./FinancialReportsPanel";
import { WhatsAppConfigPanel } from "./WhatsAppConfigPanel";
import { ChartBar, CurrencyDollar, TrendDown, Target, Gear, Medal, Clock, ChartLineUp, WhatsappLogo, Wrench, Wallet, HandCoins } from "@phosphor-icons/react";

export default function AdministrationModule() {
  const { user, token } = useAuth();
  const isAdmin = user?.role === "admin";
  const [tab, setTab] = useState(isAdmin ? "dashboard" : "incomes");
  const [year, setYear] = useState(new Date().getFullYear());
  const [month, setMonth] = useState(null);
  const [categories, setCategories] = useState([]);

  const loadCategories = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/expense-categories`, { headers: { Authorization: `Bearer ${token}` } });
      setCategories(data);
    } catch (e) { process.env.NODE_ENV === "development" && console.error(e.message); }
  }, [token]);

  useEffect(() => { loadCategories(); }, [loadCategories]);

  const tabs = [
    { id: "dashboard", label: "Dashboard", icon: ChartBar, show: isAdmin },
    { id: "incomes", label: "Ingresos", icon: CurrencyDollar, show: true },
    { id: "expenses", label: "Gastos", icon: TrendDown, show: true },
    { id: "petty-cash", label: "Caja Chica", icon: Wallet, show: true },
    { id: "services", label: "Servicios", icon: Wrench, show: true },
    { id: "sales-commissions", label: "Comisiones Ventas", icon: HandCoins, show: true },
    { id: "reports", label: "Reportes", icon: ChartLineUp, show: isAdmin },
    { id: "commissions", label: "Comisiones", icon: Medal, show: true },
    { id: "attendance", label: "Asistencia", icon: Clock, show: true },
    { id: "goals", label: "Metas", icon: Target, show: isAdmin },
    { id: "categories", label: "Categorias", icon: Gear, show: isAdmin },
    { id: "whatsapp", label: "WhatsApp", icon: WhatsappLogo, show: isAdmin },
  ];

  const months = ["","Enero","Febrero","Marzo","Abril","Mayo","Junio","Julio","Agosto","Septiembre","Octubre","Noviembre","Diciembre"];

  return (
    <div className="space-y-4" data-testid="administration-module">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-bold text-slate-900">Administracion</h1>
        <div className="flex items-center gap-2">
          <select value={year} onChange={e => setYear(+e.target.value)} className="px-3 py-1.5 border rounded-lg text-sm" data-testid="admin-year-filter">
            {[2024, 2025, 2026, 2027].map(y => <option key={y} value={y}>{y}</option>)}
          </select>
          <select value={month || ""} onChange={e => setMonth(e.target.value ? +e.target.value : null)} className="px-3 py-1.5 border rounded-lg text-sm" data-testid="admin-month-filter">
            <option value="">Todo el ano</option>
            {months.slice(1).map((m, i) => <option key={i+1} value={i+1}>{m}</option>)}
          </select>
        </div>
      </div>

      <div className="flex gap-1 bg-slate-100 p-1 rounded-lg w-fit flex-wrap" data-testid="admin-tabs">
        {tabs.filter(t => t.show).map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors flex items-center gap-1.5 ${tab === t.id ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}
            data-testid={`admin-tab-${t.id}`}>
            <t.icon size={16} weight="duotone" /> {t.label}
          </button>
        ))}
      </div>

      {tab === "dashboard" && isAdmin && <><PendingApprovalsPanel /><AdminDashboard year={year} month={month} /></>}
      {tab === "incomes" && <IncomesPanel year={year} month={month} />}
      {tab === "expenses" && <ExpensesPanel year={year} month={month} categories={categories} onCategoriesChanged={loadCategories} />}
      {tab === "petty-cash" && <PettyCashPanel year={year} month={month} />}
      {tab === "services" && <ServicesPanel year={year} month={month} />}
      {tab === "sales-commissions" && <SalesCommissionsPanel year={year} month={month} />}
      {tab === "reports" && <FinancialReportsPanel year={year} month={month} />}
      {tab === "commissions" && <CommissionsPanel year={year} month={month} />}
      {tab === "attendance" && <AttendancePanel year={year} month={month} />}
      {tab === "goals" && <SalesGoalsPanel year={year} />}
      {tab === "categories" && isAdmin && <CategoriesPanel categories={categories} onReload={loadCategories} />}
      {tab === "whatsapp" && isAdmin && <WhatsAppConfigPanel />}
    </div>
  );
}

function CategoriesPanel({ categories, onReload }) {
  const { token } = useAuth();
  const [name, setName] = useState("");
  const [color, setColor] = useState("#3B82F6");

  const handleAdd = async () => {
    if (!name.trim()) return;
    try {
      await axios.post(`${API}/admin/expense-categories`, { name, color }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Categoria creada"); setName(""); onReload();
    } catch { toast.error("Error"); }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/admin/expense-categories/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Eliminada"); onReload();
    } catch { toast.error("Error"); }
  };

  return (
    <div className="bg-white rounded-xl border p-6 max-w-lg space-y-4" data-testid="categories-panel">
      <h2 className="font-semibold text-slate-700">Categorias de Gastos</h2>
      <div className="flex gap-2">
        <input value={name} onChange={e => setName(e.target.value)} placeholder="Nombre" className="flex-1 px-3 py-2 border rounded-lg text-sm" data-testid="cat-name-input" />
        <input type="color" value={color} onChange={e => setColor(e.target.value)} className="w-10 h-10 rounded border cursor-pointer" />
        <button onClick={handleAdd} className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700" data-testid="cat-add-btn">Agregar</button>
      </div>
      <div className="space-y-2">
        {categories.map(c => (
          <div key={c.id} className="flex items-center justify-between p-2 bg-slate-50 rounded-lg">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full" style={{ backgroundColor: c.color }} />
              <span className="text-sm">{c.name}</span>
            </div>
            <button onClick={() => handleDelete(c.id)} className="text-red-500 text-xs hover:underline" data-testid={`cat-del-${c.id}`}>Eliminar</button>
          </div>
        ))}
      </div>
    </div>
  );
}
